import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import AdminPanelContext from 'utils/context/AdminPanelContex';

import Button from 'components/atom/Button';
import { IconEnum } from 'components/common/Icon';
import InputContainer from 'components/common/InputContainer';
import TextInput from 'components/common/Inputs/TextInput';
import { activeNotification } from 'components/functions/activeNotification';

const AddAgentModalContainer = styled.div`
    padding: 30px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    disply: flex;


    @media (max-width: 768px) {
        padding: 20px;
        max-height: 95vh;
    }
`;

const ModalTitle = styled.h2`
    margin-bottom: 30px;
    text-align: center;
    color: var(--color-primary);
`;

const FormContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    margin-bottom: 30px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const FullWidthItem = styled.div`
    grid-column: span 2;
    margin-top: 20px;
    
    @media (max-width: 768px) {
        grid-column: span 1;
    }
`;

const ButtonsContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;    
    gap: 15px;
    width: 50%;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        width: 100%;
    }
`;

interface AddAgentFormProps {
    onClose: () => void;
}

const AddAgentForm: React.FC<AddAgentFormProps> = ({ onClose }) => {
    const {
        adminPanelState,
        createAgent,
        createAgentFieldsState,
        handleChange,
        validateCreateAgentFields,
        clearFields
    } = useContext(AdminPanelContext);

    const [triggerValidation, setTriggerValidation] = useState(false);

    const handleSubmit = async () => {
        setTriggerValidation(true);
        const errors = validateCreateAgentFields(createAgentFieldsState);
        const hasErrors = Object.values(errors).some((error) => error !== '');

        if (hasErrors) {
            activeNotification('Dane', 'Wypełnij poprawnie wszystkie wymagane pola', 'warning');

            return;
        }
        const success = await createAgent(createAgentFieldsState);

        if (success) {
            handleCancel();
        }
    };

    const handleCancel = () => {
        clearFields();
        setTriggerValidation(false);
        onClose();
    };

    return (
        <AddAgentModalContainer>
            <ModalTitle>Dodaj agenta</ModalTitle>
            <FormContainer>
                <InputContainer title="Imię" icon={IconEnum.USER} required>
                    <TextInput
                        value={createAgentFieldsState.firstName || ''}
                        onChange={(e) => handleChange('firstName', e.target.value)}
                        placeholder="Wprowadź imię agenta..."
                        required
                        errorMessage={validateCreateAgentFields(createAgentFieldsState).firstName}
                        inputValidation={() => !!validateCreateAgentFields(createAgentFieldsState).firstName}
                        triggerValidation={triggerValidation}
                    />
                </InputContainer>

                <InputContainer title="Nazwisko" icon={IconEnum.USER} required>
                    <TextInput
                        value={createAgentFieldsState.lastName || ''}
                        onChange={(e) => handleChange('lastName', e.target.value)}
                        placeholder="Wprowadź nazwisko agenta..."
                        required
                        errorMessage={validateCreateAgentFields(createAgentFieldsState).lastName}
                        inputValidation={() => !!validateCreateAgentFields(createAgentFieldsState).lastName}
                        triggerValidation={triggerValidation}
                    />
                </InputContainer>

                <InputContainer title="Email" icon={IconEnum.EMAIL} required>
                    <TextInput
                        value={createAgentFieldsState.email || ''}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="Wprowadź adres email..."
                        required
                        errorMessage={validateCreateAgentFields(createAgentFieldsState).email}
                        inputValidation={() => !!validateCreateAgentFields(createAgentFieldsState).email}
                        triggerValidation={triggerValidation}
                    />
                </InputContainer>

                <InputContainer title="Numer telefonu" icon={IconEnum.PHONE} required>
                    <TextInput
                        value={createAgentFieldsState.phoneNumber || ''}
                        onChange={(e) => {
                            const onlyDigits = e.target.value.replace(/\D/g, '');
                            handleChange('phoneNumber', onlyDigits);
                        }}
                        placeholder="Wprowadź 9 cyfrowy numer telefonu..."
                        required
                        maxLength={9}
                        errorMessage={validateCreateAgentFields(createAgentFieldsState).phoneNumber}
                        inputValidation={() => !!validateCreateAgentFields(createAgentFieldsState).phoneNumber}
                        triggerValidation={triggerValidation}
                    />
                </InputContainer>

                <InputContainer title="Liczba SMS" icon={IconEnum.MESSAGE} helperText={`Dostępna pula SMS: ${adminPanelState.totalSmsAvailableToNewAccounts}`} required>
                    <TextInput
                        value={createAgentFieldsState.smsPackage ? createAgentFieldsState.smsPackage.toString() : '0'}
                        onChange={(e) => handleChange('smsPackage', parseInt(e.target.value) || 0)}
                        placeholder="Wprowadź liczbę SMS..."
                        required
                        errorMessage={validateCreateAgentFields(createAgentFieldsState).smsPackage}
                        inputValidation={() => !!validateCreateAgentFields(createAgentFieldsState).smsPackage}
                        triggerValidation={triggerValidation}
                    />
                </InputContainer>

                <InputContainer title="Hasło" icon={IconEnum.BLOCKED} required>
                    <TextInput
                        type="password"
                        value={createAgentFieldsState.password || ''}
                        onChange={(e) => handleChange('password', e.target.value)}
                        placeholder="Min. 8 znaków, duża litera i cyfra"
                        required
                        errorMessage={validateCreateAgentFields(createAgentFieldsState).password}
                        inputValidation={() => !!validateCreateAgentFields(createAgentFieldsState).password}
                        triggerValidation={triggerValidation}
                    />
                </InputContainer>

                <InputContainer title="Potwierdź hasło" icon={IconEnum.BLOCKED} required>
                    <TextInput
                        type="password"
                        value={createAgentFieldsState.confirmPassword || ''}
                        onChange={(e) => handleChange('confirmPassword', e.target.value)}
                        placeholder="Potwierdź hasło..."
                        required
                        errorMessage={validateCreateAgentFields(createAgentFieldsState).confirmPassword}
                        inputValidation={() => !!validateCreateAgentFields(createAgentFieldsState).confirmPassword}
                        triggerValidation={triggerValidation}
                    />
                </InputContainer>

                <FullWidthItem>
                    <ButtonsContainer>
                        <Button important onClick={handleCancel}>Anuluj</Button>
                        <Button onClick={handleSubmit}>Zapisz</Button>
                    </ButtonsContainer>
                </FullWidthItem>
            </FormContainer>
        </AddAgentModalContainer>
    );
};

export default AddAgentForm;
